export function flattenObject(
  obj: any,
  prefix = '',
  result: Record<string, any> = {}
): Record<string, any> {
  Object.entries(obj).forEach(([key, value]) => {
    const flattenedKey = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      flattenObject(value, flattenedKey, result);
    } else {
      result[flattenedKey] = value;
    }
  });

  return result;
}

export function mapById<T extends { id: string | number }>(
  arr: T[]
): Record<string | number, T> {
  return arr.reduce(
    (acc, item) => {
      acc[item.id] = item;
      return acc;
    },
    {} as Record<string | number, T>
  );
}

export function mapByUuid<T extends { uuid: string | number }>(
  arr: T[]
): Record<string | number, T> {
  return arr.reduce(
    (acc, item) => {
      acc[item.uuid] = item;
      return acc;
    },
    {} as Record<string | number, T>
  );
}

export function mapBy<T>(arr: T[], key: keyof T): Record<string, T> {
  return arr.reduce(
    (acc, item) => {
      acc[item[key] as string] = item;
      return acc;
    },
    {} as Record<string, T>
  );
}

export function dotKeysToNestedObject(obj: Record<string, any>): any {
  const result: Record<string, any> = {};

  for (const [dotKey, value] of Object.entries(obj)) {
    const keys = dotKey.split('.');
    keys.reduce((res, key, idx) => {
      if (idx === keys.length - 1) {
        res[key] = value;
      } else if (!res[key] || typeof res[key] !== 'object') {
        res[key] = {};
      }
      return res[key];
    }, result);
  }

  return result;
}

export function filterNullOrUndefined(
  obj: Record<string, any>
): Record<string, any> {
  return Object.fromEntries(
    Object.entries(obj).filter(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([_, value]) => value !== null && value !== undefined
    )
  );
}

type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<SnakeToCamelCase<U>>}`
  : S;

type DeepSnakeToCamelCase<T> = T extends object
  ? T extends Array<infer U>
    ? Array<DeepSnakeToCamelCase<U>>
    : {
        [K in keyof T as SnakeToCamelCase<string & K>]: T[K] extends object
          ? DeepSnakeToCamelCase<T[K]>
          : T[K] extends undefined
            ? undefined
            : T[K];
      }
  : T extends undefined
    ? undefined
    : T;

function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

export function convertSnakeToCamelCase<T>(obj: T): DeepSnakeToCamelCase<T> {
  if (obj === null || obj === undefined) {
    return undefined as DeepSnakeToCamelCase<T>;
  }

  if (Array.isArray(obj)) {
    return obj.map(convertSnakeToCamelCase) as DeepSnakeToCamelCase<T>;
  }

  if (obj && typeof obj === 'object') {
    const result: Record<string, any> = {};

    for (const [key, value] of Object.entries(obj)) {
      const camelKey = toCamelCase(key);
      result[camelKey] = convertSnakeToCamelCase(value);
    }

    return result as DeepSnakeToCamelCase<T>;
  }

  return obj as DeepSnakeToCamelCase<T>;
}
